import type { Decimal } from 'decimal.js';
import { CostSummary, SitePriceInfo, subscriptionAPI } from '@hotjar/webapp/billing/subscription';
import type { OwnedAccount } from '@hotjar/account';
import { logError } from '@hotjar/insights-common/lib/logger';
import type { BillingPeriod } from '@hotjar/subscription-management';
import { useEffect, useState } from 'react';
import { mapV3BillingPeriodToV2BillingCycle } from '@hotjar/upsell';

interface PlanIdsBySite {
  [siteId: string]: string | null | undefined;
}

interface TotalPrice {
  withoutDiscounts: Decimal;
  withDiscounts: Decimal;
}

interface SitesPrices {
  [siteId: string]: TotalPrice;
}

interface Props {
  account: OwnedAccount | undefined;
  planIdsBySite: PlanIdsBySite | undefined;
  billingPeriod?: BillingPeriod;
}

interface UseSitesIndividualCost {
  fetching: boolean | undefined;
  sitesPrices: SitesPrices | null;
  totalPrice: TotalPrice | null;
}

const ANNUAL_DEDUCTION_ID = 'ANNUAL_DEDUCTION';

export function useSitesCost({
  planIdsBySite,
  account,
  billingPeriod = 'MONTHLY',
}: Props): UseSitesIndividualCost {
  const [fetching, setFetching] = useState<boolean>();
  const [sitesPrices, setSitesPrices] = useState<SitesPrices | null>(null);
  const [totalPrice, setTotalPrice] = useState<TotalPrice | null>(null);

  useEffect(() => {
    const getCost = async () => {
      if (!account || !planIdsBySite) {
        return;
      }

      setFetching(true);
      try {
        const costSpecs = Object.keys(planIdsBySite)
          .map((siteId: string) => {
            const planId = planIdsBySite[siteId];

            if (!planId) {
              return undefined;
            }

            return {
              site_id: siteId,
              plan_id: planId,
              always_on_rate: 0,
            };
          })
          .filter(
            (costSpec): costSpec is Exclude<typeof costSpec, undefined> =>
              typeof costSpec !== 'undefined',
          );

        const { future: priceSummary } = await subscriptionAPI.preview(
          account,
          costSpecs,
          mapV3BillingPeriodToV2BillingCycle(billingPeriod),
          undefined,
        );

        const annualDeduction = priceSummary.deductions.find(
          (deduction) => deduction.id === ANNUAL_DEDUCTION_ID,
        );
        const annualDeductionPercentage = annualDeduction
          ? parseInt(annualDeduction.percentage, 10)
          : 0;

        const productCostsBySite = Object.keys(planIdsBySite).reduce((prev, currSiteId) => {
          const sitePrice = SitePriceInfo.fromLineItem(
            priceSummary.lineItems.find(
              (item) => item.sitePlanInfo.siteReference.siteId === parseInt(currSiteId, 10),
            ),
          );

          return {
            ...prev,
            [currSiteId]: {
              withoutDiscounts: sitePrice.subtotal,
              withDiscounts:
                billingPeriod === 'ANNUALLY'
                  ? sitePrice.subtotal.times(1 - annualDeductionPercentage / 100)
                  : sitePrice.total,
            },
          };
        }, {});

        setSitesPrices(productCostsBySite);

        const costSummary = CostSummary.fromPriceSummary(priceSummary);
        setTotalPrice({
          withoutDiscounts: costSummary.subtotal,
          withDiscounts: costSummary.total,
        });
      } catch {
        logError(new Error('useSitesCost: Could not fetch price for sites.'));
      } finally {
        setFetching(false);
      }
    };
    getCost();
  }, [planIdsBySite, account, billingPeriod]);

  return {
    fetching,
    sitesPrices,
    totalPrice,
  };
}
